import React, { useRef, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useRecoilValue, useRecoilCallback } from 'recoil'
import Pagination from '../Pagination'
import ExportButton from '../ExportButton'

import {
  Badge,
  Checkbox,
  EmptyState,
  FixedContextMenu,
  ModalConfirm,
  toastShow,
  TOAST_VARIANT_SUCCESS,
  MessageNoResult,
  Panel,
  DataListHeader,
  DataListRow,
  Flex,
  FlexItem
} from 'mailup-web-components'

import { beMessApiDeleteMessage } from 'Api/BeMessApi'

import { searchState } from '../recoil/state/search'
import { pageState, pageErrorState } from '../recoil/state/page'
import {
  resultsState,
  singleResultSelector,
  selectionState,
  allCheckedSelector,
  resultsStateEmptyState,
  fixedContextMenuVisibleSelector
} from '../recoil/state/results'
import { fetchMessages } from '../recoil/actions/actions'
import { headerClickCallback } from '../recoil/callbacks/callbacks'

import { Result } from './components'
import evalCondition from 'Helpers/evalCondition'
import { notifyParent } from 'Helpers/notifyParent'

import styled from '@emotion/styled'

const initialDeleteModalState = { open: false, ids: [], subjects: [] }

const Results = () => {
  const intl = useIntl()
  const { orderBy, orderDesc } = useRecoilValue(searchState)
  const isEmptyState = useRecoilValue(resultsStateEmptyState)
  const results = useRecoilValue(resultsState)
  const selection = useRecoilValue(selectionState)
  const allChecked = useRecoilValue(allCheckedSelector)
  const fixedContextMenuVisible = useRecoilValue(
    fixedContextMenuVisibleSelector
  )
  const [deleteModalState, setDeleteModalState] = useState(
    initialDeleteModalState
  )
  const { current: userHasStats } = useRef(evalCondition('uo:STAT'))

  const handleHeaderClick = useRecoilCallback(headerClickCallback)

  const handleDeleteAction = ids => {
    setDeleteModalState({ open: true, ids })
  }

  const handleDeleteModalConfirm = useRecoilCallback(
    ({ snapshot, set, reset }) => async () => {
      const singleMessage = await snapshot.getPromise(
        singleResultSelector(deleteModalState.ids[0])
      )
      const searchForm = await snapshot.getPromise(searchState)

      try {
        set(pageState, prev => ({
          ...prev,
          fetchingResults: true
        }))

        const calls = deleteModalState.ids.map(idMessage =>
          beMessApiDeleteMessage(idMessage)
        )

        await Promise.all(calls)

        const msg =
          deleteModalState.ids.length > 1 ? (
            <FormattedMessage
              id='Load.ActiveRecipientSummary.SuccessDeleteMessageMultiple'
              values={{
                strong: chunks => <strong>{chunks}</strong>,
                MESSAGECOUNT: deleteModalState.ids.length
              }}
            />
          ) : singleMessage ? (
            <FormattedMessage
              id='Load.ActiveRecipientSummary.SuccessDeleteMessageSingle'
              values={{
                strong: chunks => <strong>{chunks}</strong>,
                MGSNAME: singleMessage.Subject
              }}
            />
          ) : (
            intl.formatMessage({
              id: 'Load.ActiveRecipientSummary.SuccessDeleteMessageSingluar'
            })
          )

        const results = await fetchMessages(searchForm)
        set(selectionState, [])
        set(resultsState, {
          items: results.Items,
          totalItems: results.TotalItems
        })
        //  in caso di cambio pagina in seguito a cancellazione
        set(searchState, prev => ({ ...prev, pageNumber: results.PageNumber }))

        toastShow(msg, TOAST_VARIANT_SUCCESS, 'deleteMessage', true)
      } catch (e) {
        set(pageErrorState, {
          error: intl.formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
          severity: 'danger'
        })
      } finally {
        set(pageState, prev => ({
          ...prev,
          fetchingResults: false
        }))
        setDeleteModalState(initialDeleteModalState)
      }
    }
  )

  const handleDeleteModalCancel = () => {
    setDeleteModalState(initialDeleteModalState)
  }

  const handleStatsAction = idMessage => {
    const url = `statistics_app_redirector.aspx?r=/stats/sendouts/sending/mode/messages/0/${idMessage}`
    notifyParent('redirectMainFrame', [url])
  }

  const handleCheckAll = useRecoilCallback(({ set }) => e => {
    set(allCheckedSelector, e.target.checked)
  })

  const handleFixedMenuDiscard = useRecoilCallback(({ set }) => e => {
    set(allCheckedSelector, false)
  })

  const fixedActions = [
    {
      label: intl.formatMessage({ id: 'Load.MailupCommon.Delete' }),
      variant: 'danger',
      onClick: () => {
        handleDeleteAction(selection)
      },
      'data-mu-test': 'fixed-menu__delete',
      className: 'test-fixed-menu__delete'
    }
  ]

  const hasResults = !!results.items.length

  if (!hasResults && !isEmptyState) {
    return (
      <MessageNoResult
        text={`${intl.formatMessage({
          id: 'Load.SelectEmailMessage.NoSearchResultTitle'
        })} ${intl.formatMessage({
          id: 'Load.SelectEmailMessage.NoSearchResultDescription'
        })}`}
      />
    )
  }

  if (isEmptyState) {
    return (
      <EmptyState
        className='c-smtpplus-list-empty-state'
        title={intl.formatMessage({
          id: 'Load.EmailMessageListSmtpPlus.EmptyStateTitle'
        })}
        description={intl.formatMessage({
          id: 'Load.EmailMessageListSmtpPlus.EmptyStateDescription'
        })}
      />
    )
  }

  const heads = [
    {
      label: intl.formatMessage({
        id: 'Load.EmailMessageList.SubjectHeaderColumn'
      }),
      span: 6,
      sortable: true,
      field: 'Subject',
      className: 'item'
    },
    {
      label: intl.formatMessage({
        id: 'Load.EmailMessageList.LastUpdateHeaderColumn'
      }),
      span: 3,
      sortable: true,
      field: 'LastUpdated',
      align: 'right',
      className: 'item'
    },
    {
      label: intl.formatMessage({
        id: 'Load.EmailMessageList.LastSentHeaderColumn'
      }),
      span: 3,
      sortable: true,
      field: 'LastSent',
      align: 'right',
      className: 'item'
    }
  ]

  const handleChangeColumnsOrder = field => {
    handleHeaderClick(field)
  }

  return (
    <>
      <ModalConfirm
        show={deleteModalState.open}
        title={
          deleteModalState.ids.length > 1
            ? intl.formatMessage(
                { id: 'Load.EmailMessageList.MessageConfirmDeleteTitle' },
                { COUNT: deleteModalState.ids.length }
              )
            : results &&
              deleteModalState.ids.length > 0 &&
              results.items.filter(f => f.Id === deleteModalState.ids[0])
                .length > 0
            ? intl.formatMessage(
                { id: 'Load.EmailMessageList.TitleConfirmDelete' },
                {
                  name:
                    results.items.filter(
                      f => f.Id === deleteModalState.ids[0]
                    )[0].Id +
                    ' - ' +
                    results.items.filter(
                      f => f.Id === deleteModalState.ids[0]
                    )[0].Subject
                }
              )
            : intl.formatMessage({
                id: 'Load.EmailMessageList.OneMessageConfirmDeleteTitle'
              })
        }
        message={
          deleteModalState.ids.length > 1
            ? intl.formatMessage(
                {
                  id: 'Load.EmailMessageList.MoreMessageConfirmDeleteParagraph'
                },
                {
                  strong: chunks => <strong>{chunks}</strong>
                }
              )
            : intl.formatMessage(
                { id: 'Load.EmailMessageList.MessageConfirmDelete' },
                {
                  strong: chunks => <strong>{chunks}</strong>
                }
              )
        }
        onClickConfirm={handleDeleteModalConfirm}
        onClickCancel={handleDeleteModalCancel}
        stringConfirm={intl.formatMessage({ id: 'Load.MailupCommon.Delete' })}
        stringCancel={intl.formatMessage({
          id: 'Load.ModalConfermeDelete.Cancel'
        })}
        data-mu-test='modal-confirm-delete'
        variant='delete'
      />
      <Panel>
        <Flex columnGap='24px'>
          <Checkbox
            value='all'
            checked={allChecked}
            onChange={handleCheckAll}
            data-mu-test='check-all'
          />

          <FlexItem width='100%'>
            <StyledDataListHeader
              heads={heads}
              afterWidth='28px'
              orderByStatus={orderBy}
              orderDescendingStatus={orderDesc === true}
              handleChangeColumnsOrderCallback={handleChangeColumnsOrder}
            />
          </FlexItem>
        </Flex>
        <div>
          {results.items.map(item => (
            <DataListRow key={`message-${item.Id}`}>
              <Result
                message={item}
                userHasStats={userHasStats}
                onDeleteAction={handleDeleteAction}
                onStatsAction={handleStatsAction}
              />
            </DataListRow>
          ))}
        </div>

        <Pagination />
      </Panel>
      <ExportButton />

      <StyledFixedContextMenu
        visible={fixedContextMenuVisible}
        label={
          <Flex columnGap='8px' alignItems='center'>
            <Badge type='success' kind='primary' text={selection.length} />
            {intl.formatMessage({
              id: 'Load.EmailMessageList.ActionSelectedLabel'
            })}
          </Flex>
        }
        actionDiscard={{
          label: intl.formatMessage({ id: 'Load.Common.CancelSelection' }),
          onClick: handleFixedMenuDiscard
        }}
        actions={fixedActions}
      />
    </>
  )
}

export default Results

const StyledFixedContextMenu = styled(FixedContextMenu)`
  align-items: center;
  z-index: 100;
`

const StyledDataListHeader = styled(DataListHeader)`
  > div > div:nth-child(2),
  div:nth-child(3) {
    justify-content: flex-end;
  }
`
